import {
  IAlpWithLessons,
  IIlpServiceState,
  IIlpWithLessons,
  isValidObject,
  AssignmentStatusEnum,
  ServiceStatusEnum,
  IAssessment
} from "@imaginelearning/mps-api-service";
import { UxIcon, UxIconEnum } from "@imaginelearning/mps-ux-component-library";
import { useMemo, Suspense, useState } from "react";
import { useTranslation } from "react-i18next";
import useSubject from "hooks/usesubject";
import useUser from "hooks/useuser";
import useGradeRange from "hooks/usegraderange";
import getCurrentLessonIcon, { getk5LessonIcon } from "../common/icons";
import BaseCourseMap from "./base-course-map";
import { IAssignment, getChildrenFromAssignments, getContainerChildren } from "./utils";
import useFormativeLinkPlayer from "../../../hooks/linkplayer/useFormativeLinkPlayer";
import ProjectLinkPlayer from "../../project-link-player/project-link-player";
import UnauthorizedQuizModal from "./unauthorized-quiz-modal";
import "./assessments-course-map.scss";
import useTheme from "../../../hooks/usetheme";

export interface AssessmentCourseMapProps {
  assignments: IAssessment[];
  alp: IIlpServiceState<IAlpWithLessons>;
  fetchAlp: () => void;
}

function AssessmentCourseMap({ assignments, alp, fetchAlp }: AssessmentCourseMapProps) {
  const spinner = <UxIcon icon={UxIconEnum.CIRCLE_NOTCH} />;
  const { t } = useTranslation();
  const { theme } = useTheme();
  const { isKto2, isKto5, is6to8, is6to12 } = useGradeRange();
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const { createAttempt, readyToLaunch, launchProps } = useFormativeLinkPlayer(
    fetchAlp,
    () => setShowErrorDialog(true),
    { state: alp.response?.state, assignmentID: alp.response?.assignmentInfo?.assignmentID }
  );
  const getIcon = useMemo(() => (isKto5 ? getk5LessonIcon : getCurrentLessonIcon), [isKto5]);
  const { setSubject } = useSubject();
  const { user, language } = useUser();

  const overrides = {
    introMode: "None",
    outroMode: "None"
  };

  let ilpSubject = "";
  const browserTitle = document.querySelectorAll("head title")[0];
  const titleText = `${ilpSubject} Lessons | ${process.env.REACT_APP_WEBSITE_NAME}`;
  if (browserTitle) {
    browserTitle.innerHTML = titleText;
  }

  const handleClick = async ({
    pathId,
    pathVersion,
    status
  }: {
    pathId: number;
    pathVersion: number;
    status: string;
  }) => {
    await createAttempt(pathId, overrides);
  };

  const getChildrenFromAssessments = (assignments: IAssessment[]) => {
    const readyForChildren = assignments.map((assignment) => {
      const aux: IAssignment = {
        type: "assessment",
        ...assignment,
        title: assignment.name.replace(/\//g, "/ "),
        pathId: assignment.pathID,
        renderGuidedNotes: undefined,
        complete: assignment.status === AssignmentStatusEnum.COMPLETED
      };

      return aux;
    });

    return getChildrenFromAssignments({
      assignments: readyForChildren,
      onClick: handleClick,
      ilpSubject,
      user,
      is6to12,
      is6to8,
      isKto2,
      getIcon,
      theme,
      translator: t,
      languageOverride: language
    });
  };

  const error =
    alp.status === ServiceStatusEnum.ERROR ||
    (alp.status === ServiceStatusEnum.LOADED && !(isValidObject(alp.response) && assignments.length))
      ? t("No Quizzes found for Individual Learning Path")
      : "";

  const loadChildren = () => {
    if (assignments?.length) {
      ilpSubject = (alp.response as IIlpWithLessons).subject;
      setSubject(ilpSubject);
      assignments.sort((a, b) => {
        if (a.order < b.order) return -1;
        if (a.order === b.order) return 0;
        return 1;
      });

      assignments.forEach((item, index) => {
        const aux = item;
        const isComplete = item.status === AssignmentStatusEnum.COMPLETED;
        if (index === 0 && !isComplete) {
          aux.isNext = true;
          return;
        }

        if (!isComplete && assignments[index - 1].status === AssignmentStatusEnum.COMPLETED) {
          aux.isNext = true;
        }
      });
      return getChildrenFromAssessments(assignments);
    }

    return [];
  };

  if (showErrorDialog) {
    return <UnauthorizedQuizModal showErrorDialog={showErrorDialog} setShowErrorDialog={setShowErrorDialog} />;
  }

  if (readyToLaunch && launchProps) {
    return (
      <Suspense fallback={spinner}>
        <div className="assessment-link-player">
          <ProjectLinkPlayer {...launchProps} />
        </div>
      </Suspense>
    );
  }

  return <BaseCourseMap error={error} assignments={getContainerChildren(alp.status, loadChildren)} />;
}

export default AssessmentCourseMap;
